import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faSave, faGlobe, faShieldAlt,
  faChevronDown, faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import WstgTestSelector from './WstgTestSelector';
import webTargetService from '../../services/webTargetService';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';

const tacticColors = {
  'reconnaissance': 'bg-blue-500',
  'resource-development': 'bg-indigo-500',
  'initial-access': 'bg-purple-500',
  'execution': 'bg-yellow-500',
  'persistence': 'bg-green-500',
  'privilege-escalation': 'bg-orange-500',
  'defense-evasion': 'bg-teal-500',
  'credential-access': 'bg-red-500',
  'discovery': 'bg-cyan-500',
  'lateral-movement': 'bg-pink-500',
  'collection': 'bg-amber-500',
  'command-and-control': 'bg-violet-500',
  'exfiltration': 'bg-rose-500',
  'impact': 'bg-red-700'
};

/**
 * WstgPlaybookBuilder - Full-screen overlay for building OWASP WSTG campaigns
 */
const WstgPlaybookBuilder = ({
  mode = 'blue',
  projectId,
  initialTests = [],
  initialName = '',
  initialObjective = '',
  initialDescription = '',
  initialWebTargetId = null,
  onSave,
  onCancel
}) => {
  const { darkMode } = useTheme();
  const { t } = useLanguage();

  const [name, setName] = useState(initialName);
  const [objective, setObjective] = useState(initialObjective);
  const [description, setDescription] = useState(initialDescription);
  const [selectedTests, setSelectedTests] = useState(initialTests);
  const [webTargetId, setWebTargetId] = useState(initialWebTargetId);
  const [webTargets, setWebTargets] = useState([]);
  const [showMitreSidebar, setShowMitreSidebar] = useState(true);

  useEffect(() => {
    if (projectId) {
      webTargetService.getAll(projectId).then(setWebTargets).catch(() => {});
    }
  }, [projectId]);

  // Compute MITRE coverage from selected tests' mitreMapping
  const mitreCoverage = useMemo(() => {
    const tactics = {};
    selectedTests.forEach(test => {
      (test.mitreMapping || []).forEach(m => {
        const tactic = m.tactic || 'unknown';
        if (!tactics[tactic]) tactics[tactic] = [];
        if (!tactics[tactic].find(t => t.techniqueId === m.techniqueId)) {
          tactics[tactic].push(m);
        }
      });
    });
    return tactics;
  }, [selectedTests]);

  const totalMitreTechniques = useMemo(() => {
    const ids = new Set();
    selectedTests.forEach(test => {
      (test.mitreMapping || []).forEach(m => ids.add(m.techniqueId));
    });
    return ids.size;
  }, [selectedTests]);

  const handleSave = () => {
    if (!name.trim() || !objective.trim() || selectedTests.length === 0) return;
    onSave({
      name: name.trim(),
      objective: objective.trim(),
      description: description.trim(),
      wstgTests: selectedTests,
      webTargetId: webTargetId || null
    });
  };

  const bg = darkMode ? 'bg-gray-800' : 'bg-white';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const hover = darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100';

  return (
    <div className={`flex flex-col h-full ${text}`}>
      {/* Header */}
      <div className={`${bg} border-b ${border} p-4`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {onCancel && (
              <button onClick={onCancel} className={`p-2 rounded ${hover} ${textMuted}`}>
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
            )}
            <div>
              <h2 className={`text-xl font-bold ${text}`}>
                <FontAwesomeIcon icon={faGlobe} className="mr-2 text-orange-500" />
                {t('campaigns.wstgCampaignBuilder') || 'OWASP WSTG Campaign Builder'}
              </h2>
              <p className={`text-sm ${textMuted}`}>
                {t('campaigns.wstgCampaignBuilderDesc') || 'Select OWASP WSTG tests to build a web application security assessment campaign'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm px-2 py-1 rounded bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300">
              OWASP WSTG
            </span>
            <button
              onClick={handleSave}
              disabled={!name.trim() || !objective.trim() || selectedTests.length === 0}
              className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faSave} />
              {t('common.save') || 'Save'}
            </button>
          </div>
        </div>

        {/* Campaign metadata */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className={`block text-xs mb-1 ${textMuted}`}>{t('campaigns.campaignName') || 'Campaign Name'} *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className={`w-full px-3 py-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              placeholder="e.g., OWASP Web Assessment"
            />
          </div>
          <div>
            <label className={`block text-xs mb-1 ${textMuted}`}>{t('campaigns.objective') || 'Objective'} *</label>
            <input
              type="text"
              value={objective}
              onChange={e => setObjective(e.target.value)}
              className={`w-full px-3 py-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              placeholder="e.g., Test web application for OWASP vulnerabilities"
            />
          </div>
          <div>
            <label className={`block text-xs mb-1 ${textMuted}`}>{t('common.description') || 'Description'}</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              className={`w-full px-3 py-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              placeholder="Optional description..."
            />
          </div>
          <div>
            <label className={`block text-xs mb-1 ${textMuted}`}>{t('campaigns.webTarget') || 'Web Target'}</label>
            <select
              value={webTargetId || ''}
              onChange={e => setWebTargetId(e.target.value || null)}
              className={`w-full px-3 py-2 rounded border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            >
              <option value="">None</option>
              {webTargets.map(wt => (
                <option key={wt._id} value={wt._id}>{wt.baseUrl}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden" style={{ minHeight: '60vh' }}>
        {/* WSTG Test Selector */}
        <div className={`${showMitreSidebar ? 'w-3/4' : 'w-full'} flex flex-col`}>
          <WstgTestSelector
            selectedTests={selectedTests}
            onSelectionChange={setSelectedTests}
          />
        </div>

        {/* MITRE Coverage Sidebar */}
        {showMitreSidebar && (
          <div className={`w-1/4 border-l ${border} flex flex-col overflow-hidden`}>
            <div className={`p-3 border-b ${border} flex items-center justify-between`}>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faShieldAlt} className="text-red-500" />
                <span className={`font-medium text-sm ${text}`}>
                  {t('campaigns.mitreCoverage') || 'MITRE Coverage'}
                </span>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                {totalMitreTechniques} techniques
              </span>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {Object.keys(mitreCoverage).length === 0 ? (
                <div className={`p-4 text-center ${textMuted}`}>
                  <p className="text-sm">Select WSTG tests to see MITRE ATT&CK coverage</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(mitreCoverage).map(([tactic, techniques]) => (
                    <div key={tactic}>
                      <div className="flex items-center gap-1 mb-1">
                        <span className={`w-2 h-2 rounded-full ${tacticColors[tactic] || 'bg-gray-500'}`}></span>
                        <span className={`text-xs font-medium ${text}`}>{tactic}</span>
                        <span className={`text-[10px] ${textMuted}`}>({techniques.length})</span>
                      </div>
                      <div className="flex flex-wrap gap-1 ml-3 mb-2">
                        {techniques.map((tech, i) => (
                          <span key={i} className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded ${
                            darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'
                          }`}>
                            {tech.techniqueId}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Summary bar */}
            {selectedTests.length > 0 && (
              <div className={`p-3 border-t ${border}`}>
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className={`p-2 rounded ${darkMode ? 'bg-orange-900/20' : 'bg-orange-50'}`}>
                    <div className="text-lg font-bold text-orange-500">{selectedTests.length}</div>
                    <div className={`text-[10px] ${textMuted}`}>WSTG Tests</div>
                  </div>
                  <div className={`p-2 rounded ${darkMode ? 'bg-red-900/20' : 'bg-red-50'}`}>
                    <div className="text-lg font-bold text-red-500">{totalMitreTechniques}</div>
                    <div className={`text-[10px] ${textMuted}`}>MITRE Techniques</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WstgPlaybookBuilder;
